"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type Context,
  type ReactNode,
} from "react";
import {
  LDReactContext,
  useBoolVariation,
  type LDReactClient,
  type LDReactClientContextValue,
} from "@launchdarkly/react-sdk";

type BooleanFlagOverrides = Readonly<Record<string, boolean>>;

type FeatureFlagRuntime = {
  overrides?: BooleanFlagOverrides;
  reactContext: Context<LDReactClientContextValue>;
  source: "launchdarkly" | "override";
};

const noOpClient = {
  isReady: () => false,
  off: () => undefined,
  on: () => undefined,
} as unknown as LDReactClient;

const NoOpLaunchDarklyContext = createContext<LDReactClientContextValue>({
  client: noOpClient,
  initializedState: "initializing",
});

const FeatureFlagRuntimeContext = createContext<FeatureFlagRuntime | null>(
  null,
);

/**
 * Connects application hooks to the globally mounted LaunchDarkly provider and
 * applies deterministic browser overrides installed before the app renders.
 */
export function FeatureFlagsBridge({ children }: { children: ReactNode }) {
  const overrides =
    typeof window !== "undefined" ? window.__LD_FLAG_OVERRIDES__ : undefined;
  const runtime = useMemo<FeatureFlagRuntime>(
    () => ({
      overrides,
      reactContext: LDReactContext,
      source: "launchdarkly",
    }),
    [overrides],
  );

  return (
    <FeatureFlagRuntimeContext.Provider value={runtime}>
      {children}
    </FeatureFlagRuntimeContext.Provider>
  );
}

export function FeatureFlagsTestProvider({
  children,
  flags = {},
}: {
  children: ReactNode;
  flags?: BooleanFlagOverrides;
}) {
  const runtime = useMemo<FeatureFlagRuntime>(
    () => ({
      overrides: flags,
      reactContext: NoOpLaunchDarklyContext,
      source: "override",
    }),
    [flags],
  );

  return (
    <FeatureFlagRuntimeContext.Provider value={runtime}>
      {children}
    </FeatureFlagRuntimeContext.Provider>
  );
}

let didWarnMissingProvider = false;

export function __resetMissingFeatureFlagsProviderWarningForTests(): void {
  didWarnMissingProvider = false;
}

/**
 * Reads a boolean flag by its exact LaunchDarkly key. Missing flags fail closed
 * to the supplied default (`false` by default).
 */
export function useBooleanFeatureFlag(
  flagKey: string,
  defaultValue = false,
): boolean {
  const runtime = useContext(FeatureFlagRuntimeContext);
  const isMissingProvider = runtime === null;
  const reactContext = runtime?.reactContext ?? NoOpLaunchDarklyContext;
  const launchDarklyValue = useBoolVariation(
    flagKey,
    defaultValue,
    reactContext,
  );

  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      isMissingProvider &&
      !didWarnMissingProvider
    ) {
      didWarnMissingProvider = true;
      console.warn(
        "[useBooleanFeatureFlag] called outside the LaunchDarkly provider; returning the default value.",
      );
    }
  }, [isMissingProvider]);

  const override = runtime?.overrides?.[flagKey];
  if (typeof override === "boolean") return override;

  return runtime?.source === "launchdarkly" ? launchDarklyValue : defaultValue;
}
